import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  createOfferingBookingAPI,
  createOfferingAPI,
  deleteOfferingAPI,
  deleteOfferingFileAPI,
  getAllOfferingAPI,
  getOfferingBookingByIdAPI,
  getOfferingBookingsAPI,
  getOfferingAPI,
  getSellerOfferingBookingsAPI,
  uploadOfferingFilesAPI,
  updateOfferingAPI,
  updateOfferingStatusAPI,
} from "../../api/ecommerce/OfferingApi";

const initialState = {
  offering: null,
  offerings: [],
  offeringBooking: null,
  offeringBookings: [],
  sellerOfferingBookings: [],
  loading: false,
  detailsLoading: false,
  bookingLoading: false,
  bookingDetailsLoading: false,
  sellerBookingLoading: false,
  submitting: false,
  bookingSubmitting: false,
  statusUpdating: false,
  deleting: false,
  fileUploading: false,
  fileDeleting: false,
  success: false,
};

export const getOffering = createAsyncThunk(
  "offering/getOffering",
  async (id, { rejectWithValue }) => {
    try {
      const response = await getOfferingAPI(id);
      return response?.data;
    } catch (err) {
      return rejectWithValue({
        errorMessage: err?.data?.message || err?.message,
      });
    }
  }
);

export const createOffering = createAsyncThunk(
  "offering/createOffering",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await createOfferingAPI(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue({
        errorMessage: err?.data?.message || err?.message,
      });
    }
  }
);

export const updateOffering = createAsyncThunk(
  "offering/updateOffering",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await updateOfferingAPI(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue({
        errorMessage: err?.data?.message || err?.message,
      });
    }
  }
);

export const deleteOffering = createAsyncThunk(
  "offering/deleteOffering",
  async (id, { rejectWithValue }) => {
    try {
      const response = await deleteOfferingAPI(id);
      return response?.data;
    } catch (err) {
      return rejectWithValue({
        errorMessage: err?.data?.message || err?.message,
      });
    }
  }
);

export const updateOfferingStatus = createAsyncThunk(
  "offering/updateOfferingStatus",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await updateOfferingStatusAPI(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue({
        errorMessage: err?.data?.message || err?.message,
      });
    }
  }
);

export const getAllOffering = createAsyncThunk(
  "offering/getAllOffering",
  async (params, { rejectWithValue }) => {
    try {
      const response = await getAllOfferingAPI(params);
      return response?.data;
    } catch (err) {
      return rejectWithValue({
        errorMessage: err?.data?.message || err?.message,
      });
    }
  }
);

export const deleteOfferingFile = createAsyncThunk(
  "offering/deleteOfferingFile",
  async (uploadId, { rejectWithValue }) => {
    try {
      const response = await deleteOfferingFileAPI(uploadId);
      return response?.data;
    } catch (err) {
      return rejectWithValue({
        errorMessage: err?.data?.message || err?.message,
      });
    }
  }
);

export const uploadOfferingFiles = createAsyncThunk(
  "offering/uploadOfferingFiles",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await uploadOfferingFilesAPI(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue({
        errorMessage: err?.data?.message || err?.message,
      });
    }
  }
);

export const getOfferingBookings = createAsyncThunk(
  "offering/getOfferingBookings",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getOfferingBookingsAPI();
      return response?.data;
    } catch (err) {
      return rejectWithValue({
        errorMessage: err?.data?.message || err?.message,
      });
    }
  }
);

export const createOfferingBooking = createAsyncThunk(
  "offering/createOfferingBooking",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await createOfferingBookingAPI(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue({
        errorMessage: err?.data?.message || err?.message,
      });
    }
  }
);

export const getOfferingBookingById = createAsyncThunk(
  "offering/getOfferingBookingById",
  async (bookingId, { rejectWithValue }) => {
    try {
      const response = await getOfferingBookingByIdAPI(bookingId);
      return response?.data;
    } catch (err) {
      return rejectWithValue({
        errorMessage: err?.data?.message || err?.message,
      });
    }
  }
);

export const getSellerOfferingBookings = createAsyncThunk(
  "offering/getSellerOfferingBookings",
  async (params, { rejectWithValue }) => {
    try {
      const response = await getSellerOfferingBookingsAPI(params);
      return response?.data;
    } catch (err) {
      return rejectWithValue({
        errorMessage: err?.data?.message || err?.message,
      });
    }
  }
);

const offeringSlice = createSlice({
  name: "offering",
  initialState,
  reducers: {
    clearOffering: (state) => {
      state.offering = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getOffering.pending, (state) => {
      state.detailsLoading = true;
      state.success = false;
    });
    builder.addCase(getOffering.fulfilled, (state, action) => {
      state.offering = action?.payload?.response ?? action?.payload;
      state.detailsLoading = false;
      state.success = true;
    });
    builder.addCase(getOffering.rejected, (state) => {
      state.detailsLoading = false;
      state.success = false;
    });

    builder.addCase(getAllOffering.pending, (state) => {
      state.loading = true;
      state.success = false;
    });
    builder.addCase(getAllOffering.fulfilled, (state, action) => {
      state.offerings = action?.payload?.response ?? action?.payload ?? [];
      state.loading = false;
      state.success = true;
    });
    builder.addCase(getAllOffering.rejected, (state) => {
      state.loading = false;
      state.success = false;
    });

    builder.addCase(createOffering.pending, (state) => {
      state.submitting = true;
      state.success = false;
    });
    builder.addCase(createOffering.fulfilled, (state, action) => {
      state.offering = action?.payload?.response ?? action?.payload;
      state.submitting = false;
      state.success = true;
    });
    builder.addCase(createOffering.rejected, (state) => {
      state.submitting = false;
      state.success = false;
    });

    builder.addCase(updateOffering.pending, (state) => {
      state.submitting = true;
      state.success = false;
    });
    builder.addCase(updateOffering.fulfilled, (state, action) => {
      state.offering = action?.payload?.response ?? action?.payload;
      state.submitting = false;
      state.success = true;
    });
    builder.addCase(updateOffering.rejected, (state) => {
      state.submitting = false;
      state.success = false;
    });

    builder.addCase(updateOfferingStatus.pending, (state) => {
      state.statusUpdating = true;
      state.success = false;
    });
    builder.addCase(updateOfferingStatus.fulfilled, (state, action) => {
      state.offering = action?.payload?.response ?? action?.payload;
      state.statusUpdating = false;
      state.success = true;
    });
    builder.addCase(updateOfferingStatus.rejected, (state) => {
      state.statusUpdating = false;
      state.success = false;
    });

    builder.addCase(deleteOffering.pending, (state) => {
      state.deleting = true;
      state.success = false;
    });
    builder.addCase(deleteOffering.fulfilled, (state) => {
      state.deleting = false;
      state.success = true;
    });
    builder.addCase(deleteOffering.rejected, (state) => {
      state.deleting = false;
      state.success = false;
    });

    builder.addCase(uploadOfferingFiles.pending, (state) => {
      state.fileUploading = true;
      state.success = false;
    });
    builder.addCase(uploadOfferingFiles.fulfilled, (state) => {
      state.fileUploading = false;
      state.success = true;
    });
    builder.addCase(uploadOfferingFiles.rejected, (state) => {
      state.fileUploading = false;
      state.success = false;
    });

    builder.addCase(deleteOfferingFile.pending, (state) => {
      state.fileDeleting = true;
      state.success = false;
    });
    builder.addCase(deleteOfferingFile.fulfilled, (state) => {
      state.fileDeleting = false;
      state.success = true;
    });
    builder.addCase(deleteOfferingFile.rejected, (state) => {
      state.fileDeleting = false;
      state.success = false;
    });

    builder.addCase(getOfferingBookings.pending, (state) => {
      state.bookingLoading = true;
      state.success = false;
    });
    builder.addCase(getOfferingBookings.fulfilled, (state, action) => {
      state.offeringBookings = action?.payload?.response ?? action?.payload ?? [];
      state.bookingLoading = false;
      state.success = true;
    });
    builder.addCase(getOfferingBookings.rejected, (state) => {
      state.bookingLoading = false;
      state.success = false;
    });

    builder.addCase(createOfferingBooking.pending, (state) => {
      state.bookingSubmitting = true;
      state.success = false;
    });
    builder.addCase(createOfferingBooking.fulfilled, (state, action) => {
      state.offeringBooking = action?.payload?.response ?? action?.payload;
      state.bookingSubmitting = false;
      state.success = true;
    });
    builder.addCase(createOfferingBooking.rejected, (state) => {
      state.bookingSubmitting = false;
      state.success = false;
    });

    builder.addCase(getOfferingBookingById.pending, (state) => {
      state.bookingDetailsLoading = true;
      state.success = false;
    });
    builder.addCase(getOfferingBookingById.fulfilled, (state, action) => {
      state.offeringBooking = action?.payload?.response ?? action?.payload;
      state.bookingDetailsLoading = false;
      state.success = true;
    });
    builder.addCase(getOfferingBookingById.rejected, (state) => {
      state.bookingDetailsLoading = false;
      state.success = false;
    });

    builder.addCase(getSellerOfferingBookings.pending, (state) => {
      state.sellerBookingLoading = true;
      state.success = false;
    });
    builder.addCase(getSellerOfferingBookings.fulfilled, (state, action) => {
      state.sellerOfferingBookings = action?.payload?.response ?? action?.payload ?? [];
      state.sellerBookingLoading = false;
      state.success = true;
    });
    builder.addCase(getSellerOfferingBookings.rejected, (state) => {
      state.sellerBookingLoading = false;
      state.success = false;
    });
  },
});

export const { clearOffering } = offeringSlice.actions;
export default offeringSlice.reducer;
